import React, { useEffect, useRef, useState, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage, Citation, UsageStats } from '../types';
import FeedbackBar from './FeedbackBar';
import ReportIssue from './ReportIssue';
import ConfidenceBadge from './ConfidenceBadge';
import { BookmarkButton } from './Bookmarks';
import { useToast } from '../contexts/ToastContext';
import { CROSSREF_TEST, CROSSREF_SPLIT, buildCrossRefQuery } from '../utils/crossRefParser';
import RequestStandard from './RequestStandard';
import HighlightOverlay from './HighlightOverlay';
import Checklist from './Checklist';
import ShareMenu from './ShareMenu';

interface Props {
  message: ChatMessage;
  onCitationClick: (msgId: string, num: number) => void;
  onShowSources?: () => void;
  /** True only for the last completed assistant message */
  isLast?: boolean;
  /** Called with the follow-up text when a chip is clicked */
  onFollowUp?: (query: string) => void;
  /** Original user query that triggered this assistant response */
  queryText?: string;
  /** Callback when bookmark state changes (to refresh sidebar count) */
  onBookmarkChange?: () => void;
  /** Highlight refresh key — bumped when highlights change */
  highlightRefreshKey?: number;
  /** Called when a highlight is removed via the overlay */
  onHighlightRemoved?: () => void;
}

/* ── Mobile media query hook (C3) ───────────────────────────── */

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 767px)').matches
      : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

/* ── Expandable list item (C3) ──────────────────────────────── */

interface ExpandableLiProps {
  children: React.ReactNode;
  corpusClass?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

function ExpandableLi({ children, corpusClass, ...rest }: ExpandableLiProps) {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const innerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!isMobile || expanded) return;
    const el = innerRef.current;
    if (!el) return;
    // scrollHeight is the full content height even when CSS line-clamp is applied
    setOverflows(el.scrollHeight > el.clientHeight + 3);
  }, [isMobile, expanded]);

  const liClass = [corpusClass, 'expandable-li'].filter(Boolean).join(' ');

  if (!isMobile) {
    return (
      <li className={corpusClass || undefined} {...rest}>
        {children}
      </li>
    );
  }

  return (
    <li className={liClass} {...rest}>
      <span
        ref={innerRef}
        className={`expandable-li-inner${expanded ? '' : ' expandable-li-inner--clamped'}`}
      >
        {children}
      </span>
      {(overflows || expanded) && (
        <button
          className="expandable-li-toggle"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          aria-label={expanded ? 'Show less' : 'Show more'}
        >
          {expanded ? '↑ less' : '↓ more'}
        </button>
      )}
    </li>
  );
}

/* ── Inline markup injection helpers ───────────────────────────
   Handles [N] citation badges, [MANDATORY] and [GUIDANCE] pills.
   C2: badges are now superscript style (class citation-badge--super).
   C2: [MANDATORY] → red pill, [GUIDANCE] → blue pill.
   ─────────────────────────────────────────────────────────────── */

/** R2-M9: Normative language definitions for tooltips */
const NORMATIVE_TOOLTIPS: Record<string, string> = {
  shall: '"Shall" indicates a mandatory requirement \u2014 compliance is required',
  must: '"Must" indicates a mandatory requirement \u2014 compliance is required',
  'shall not': '"Shall not" indicates a mandatory prohibition',
  should: '"Should" indicates a recommendation \u2014 strongly advised but not mandatory',
  'should not': '"Should not" indicates an advised-against practice',
  may: '"May" indicates a permitted option \u2014 at the implementer\'s discretion',
};

/* ── Cross-reference chip injection ─────────────────────────────
   Detects inline standard references (AS/NZS 3000:2018, Clause 2.3.1,
   Table 3.1 of AS 1428.1 etc.) and wraps them in clickable chips that
   fire a scoped follow-up query.
   ─────────────────────────────────────────────────────────────── */

function injectCrossRefs(
  text: string,
  keyPrefix: string,
  onCrossRef?: (query: string) => void,
): React.ReactNode[] {
  if (!CROSSREF_TEST.test(text)) {
    return injectNormativeTooltips(text, keyPrefix);
  }
  const parts = text.split(CROSSREF_SPLIT);
  const nodes: React.ReactNode[] = [];
  parts.forEach((part, i) => {
    if (!part) return; // skip empty segments from split
    if (i % 2 === 1) {
      // Matched cross-reference — render as chip
      nodes.push(
        <span
          key={`${keyPrefix}-xr-${i}`}
          className="crossref-chip"
          role="link"
          tabIndex={0}
          onClick={(e) => {
            e.currentTarget.setAttribute('data-visited', 'true');
            onCrossRef?.(buildCrossRefQuery(part));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              (e.target as HTMLElement).setAttribute('data-visited', 'true');
              onCrossRef?.(buildCrossRefQuery(part));
            }
          }}
          title={`Look up ${part}`}
        >
          {part}
        </span>,
      );
    } else {
      // Plain text — pass through normative tooltips
      nodes.push(
        <React.Fragment key={`${keyPrefix}-xr-${i}`}>
          {injectNormativeTooltips(part, `${keyPrefix}-xr-${i}`)}
        </React.Fragment>,
      );
    }
  });
  return nodes;
}

/** Inject normative-language tooltips into a text fragment */
function injectNormativeTooltips(text: string, keyPrefix: string): React.ReactNode[] {
  // Match shall not, should not, shall, must, should, may (case-insensitive, word boundaries)
  const regex = /\b(shall\s+not|should\s+not|shall|must|should|may)\b/gi;
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  let idx = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<React.Fragment key={`${keyPrefix}-nt-${idx++}`}>{text.slice(lastIdx, match.index)}</React.Fragment>);
    }
    const word = match[0];
    const tooltip = NORMATIVE_TOOLTIPS[word.toLowerCase()] || '';
    parts.push(
      <abbr
        key={`${keyPrefix}-nt-${idx++}`}
        className="normative-term"
        title={tooltip}
      >
        {word}
      </abbr>
    );
    lastIdx = regex.lastIndex;
  }
  if (lastIdx < text.length) {
    parts.push(<React.Fragment key={`${keyPrefix}-nt-${idx}`}>{text.slice(lastIdx)}</React.Fragment>);
  }
  return parts.length > 0 ? parts : [<React.Fragment key={`${keyPrefix}-nt-0`}>{text}</React.Fragment>];
}

function injectInlineMarkup(
  text: string,
  msgId: string,
  onClick: (msgId: string, num: number) => void,
  keyPrefix: string,
  onCrossRef?: (query: string) => void,
): React.ReactNode[] {
  const parts = text.split(/(\[\d+\]|\[MANDATORY\]|\[GUIDANCE\])/g);
  return parts.map((part, i) => {
    const citationMatch = part.match(/^\[(\d+)\]$/);
    if (citationMatch) {
      const num = parseInt(citationMatch[1], 10);
      return (
        <button
          key={`${keyPrefix}-${i}`}
          className="citation-badge citation-badge--super"
          onClick={(e) => { e.stopPropagation(); onClick(msgId, num); }}
          aria-label={`Citation ${num}`}
        >
          {num}
        </button>
      );
    }
    if (part === '[MANDATORY]') {
      return (
        <span
          key={`${keyPrefix}-${i}`}
          className="tag-pill tag-pill--mandatory"
          title="This requirement uses mandatory language (shall/must) \u2014 compliance is required"
        >
          MANDATORY
        </span>
      );
    }
    if (part === '[GUIDANCE]') {
      return (
        <span
          key={`${keyPrefix}-${i}`}
          className="tag-pill tag-pill--guidance"
          title="This is advisory guidance (should/may) \u2014 recommended but not mandatory"
        >
          GUIDANCE
        </span>
      );
    }
    // R2-M9: Apply cross-ref + normative language tooltips to remaining text fragments
    return <React.Fragment key={`${keyPrefix}-${i}`}>{injectCrossRefs(part, `${keyPrefix}-${i}`, onCrossRef)}</React.Fragment>;
  });
}

/** Recursively walk React children and inject inline markup into text nodes. */
function processChildren(
  children: React.ReactNode,
  msgId: string,
  onClick: (msgId: string, num: number) => void,
  depth: string,
  onCrossRef?: (query: string) => void,
): React.ReactNode {
  return React.Children.map(children, (child, idx) => {
    if (typeof child === 'string') {
      if (/\[\d+\]|\[MANDATORY\]|\[GUIDANCE\]/.test(child)) {
        return <>{injectInlineMarkup(child, msgId, onClick, `${depth}-${idx}`, onCrossRef)}</>;
      }
      // Check for cross-references even in text without citations/tags
      if (CROSSREF_TEST.test(child)) {
        return <>{injectCrossRefs(child, `${depth}-${idx}`, onCrossRef)}</>;
      }
      return child;
    }
    return child;
  });
}

/* ── Corpus gap helpers (C5) ────────────────────────────────── */

/** Recursively extract plain text from React node tree. */
function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node || typeof node !== 'object') return '';
  if (Array.isArray(node)) return node.map(extractText).join('');
  if ('props' in (node as React.ReactElement)) {
    return extractText((node as React.ReactElement).props.children as React.ReactNode);
  }
  return '';
}

/**
 * Return a CSS class name for corpus-gap styling, or null.
 * C5: ⬜/❌ → not-in-corpus (amber), ✅ → in-corpus (green), 📂 → header.
 */
function getCorpusGapClass(text: string): string | null {
  const t = text.trimStart();
  if (t.startsWith('⬜') || t.startsWith('❌')) return 'corpus-gap--not-in';
  if (t.startsWith('✅')) return 'corpus-gap--in';
  if (t.startsWith('📂')) return 'corpus-gap--header';
  return null;
}

/** R2-H7: Tooltip text for corpus gap symbols */
function getCorpusGapTooltip(text: string): string | null {
  const t = text.trimStart();
  if (t.startsWith('⬜')) return 'Not currently in the indexed corpus \u2014 this standard may need to be added';
  if (t.startsWith('❌')) return 'Not available in the indexed corpus';
  if (t.startsWith('✅')) return 'This standard is indexed and searchable in the corpus';
  if (t.startsWith('📂')) return 'Corpus coverage summary';
  return null;
}

/* ── react-markdown component overrides ─────────────────────── */

function useCitationComponents(
  msgId: string,
  onClick: (msgId: string, num: number) => void,
  onCrossRef?: (query: string) => void,
): Components {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  return useMemo(() => {
    const wrap = (Tag: string): any => {
      const Comp = (props: any) => {
        const { children, node: _node, ...rest } = props;
        return React.createElement(
          Tag,
          rest,
          processChildren(children as React.ReactNode, msgId, onClick, Tag, onCrossRef),
        );
      };
      Comp.displayName = `Citation_${Tag}`;
      return Comp;
    };

    // C3 + C5: list items — expandable on mobile + corpus gap colouring + R2-H7 tooltips
    const LiComp = (props: any) => {
      const { children, node: _node, ordered: _ordered, ...rest } = props;
      const rawText = extractText(children as React.ReactNode);
      const corpusClass = getCorpusGapClass(rawText);
      const tooltip = getCorpusGapTooltip(rawText);
      const processed = processChildren(children as React.ReactNode, msgId, onClick, 'li', onCrossRef);
      return (
        <ExpandableLi corpusClass={corpusClass} title={tooltip || undefined} {...rest}>
          {processed}
        </ExpandableLi>
      );
    };
    LiComp.displayName = 'Citation_li';

    // C5: paragraphs — corpus gap block colouring + R2-H7 tooltips
    const PComp = (props: any) => {
      const { children, node: _node, ...rest } = props;
      const rawText = extractText(children as React.ReactNode);
      const corpusClass = getCorpusGapClass(rawText);
      const tooltip = getCorpusGapTooltip(rawText);
      const processed = processChildren(children as React.ReactNode, msgId, onClick, 'p', onCrossRef);
      if (corpusClass) {
        return <div className={`corpus-gap-block ${corpusClass}`} title={tooltip || undefined}>{processed}</div>;
      }
      return React.createElement('p', rest, processed);
    };
    PComp.displayName = 'Citation_p';

    // R2-H2: "Direct Answer" heading gets visual elevation
    const H2Comp = (props: any) => {
      const { children, node: _node, ...rest } = props;
      const rawText = extractText(children as React.ReactNode);
      const isDirectAnswer = /^direct\s+answer/i.test(rawText.trim());
      const processed = processChildren(children as React.ReactNode, msgId, onClick, 'h2', onCrossRef);
      return (
        <h2
          {...rest}
          className={isDirectAnswer ? 'direct-answer-heading' : undefined}
        >
          {processed}
        </h2>
      );
    };
    H2Comp.displayName = 'Citation_h2';

    return {
      p: PComp,
      li: LiComp,
      td: wrap('td'),
      th: wrap('th'),
      strong: wrap('strong'),
      em: wrap('em'),
      h1: wrap('h1'),
      h2: H2Comp,
      h3: wrap('h3'),
      h4: wrap('h4'),
    };
  }, [msgId, onClick, onCrossRef]);
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

/* ── Markdown stripper (for plain-text copy) ────────────────────── */

function stripMarkdown(md: string): string {
  return md
    .replace(/^#{1,6}\s+/gm, '')           // # headers
    .replace(/\*\*(.+?)\*\*/g, '$1')        // **bold**
    .replace(/\*(.+?)\*/g, '$1')            // *italic*
    .replace(/__(.+?)__/g, '$1')            // __bold__
    .replace(/_(.+?)_/g, '$1')              // _italic_
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [text](url) → text
    .replace(/^[-*+]\s+/gm, '')             // bullet list markers
    .replace(/^\d+\.\s+/gm, '')             // numbered list markers
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1') // `code` / ```code```
    .replace(/^>\s*/gm, '')                 // blockquotes
    .trim();
}

/* ── Usage footer helpers ────────────────────────────────────────── */

function formatUsageFooter(usage: UsageStats, elapsed?: number): string {
  const m = usage.model.toLowerCase();
  const modelName = m.includes('opus') ? 'Opus'
    : m.includes('sonnet') ? 'Sonnet'
    : m.includes('haiku') ? 'Haiku'
    : usage.model;

  const inputK  = (usage.inputTokens  / 1000).toFixed(1);
  const outputK = (usage.outputTokens / 1000).toFixed(1);

  // F37: claude-opus-4-8 pricing is $5/M input + $25/M output (was 3× overstated)
  const inputCostPerM  = m.includes('opus') ? 5 : m.includes('sonnet') ? 3 : 1;
  const outputCostPerM = m.includes('opus') ? 25 : m.includes('sonnet') ? 15 : 5;
  const cost = (usage.inputTokens / 1_000_000 * inputCostPerM)
             + (usage.outputTokens / 1_000_000 * outputCostPerM);
  const costStr = `~$${cost.toFixed(2)}`;

  const parts = [modelName, `${inputK}K in`, `${outputK}K out`, costStr];
  if (elapsed !== undefined) {
    parts.push(`${(elapsed / 1000).toFixed(1)}s`);
  }
  return parts.join(' · ');
}

/* ── Content sanitiser — fix malformed markdown before rendering ─── */

/**
 * Fix common markdown rendering bugs in LLM output:
 * - "**Cross-references" without closing bold (renders as literal "**Cross")
 * - Orphaned bold markers around section headings
 */
function sanitiseContent(raw: string): string {
  let s = raw;

  // Fix: "**Cross-references ..." without closing ** — turn into a proper heading
  s = s.replace(
    /^(\*\*Cross[\s-]?[Rr]eferences?)(?:\s*(?:not\s+in\s+retrieved\s+sources|not\s+in\s+the\s+retrieved\s+set|referenced\s+but\s+not\s+retrieved))?([:.]?)\s*(?:\*\*)?$/gm,
    (_match, prefix: string, punct: string) => {
      const label = prefix.replace(/^\*\*/, '');
      return `### ${label} — Not in retrieved sources${punct}`;
    },
  );

  // Fix: any remaining orphaned "**Text" without closing ** on the same line
  s = s.replace(
    /^(\*\*)([A-Z][^*\n]{3,})$/gm,
    (_match, _stars: string, text: string) => `### ${text}`,
  );

  return s;
}

/* ── FOLLOW_UPS parser ─────────────────────────────────────────── */

function parseFollowUps(content: string): { cleanContent: string; followUps: string[] } {
  const match = content.match(/<!--\s*FOLLOW_UPS\s*-->([\s\S]*?)<!--\s*\/FOLLOW_UPS\s*-->/);

  let clean = match
    ? content.replace(/<!--\s*FOLLOW_UPS\s*-->[\s\S]*?<!--\s*\/FOLLOW_UPS\s*-->/, '').trim()
    : content;

  // Strip confidence comments (delivered via separate SSE event, not displayed inline)
  clean = clean
    .replace(/<!--\s*CONFIDENCE:.*?-->/g, '')
    .replace(/<!--\s*CONFIDENCE_REASON:.*?-->/g, '')
    .trim();

  // Fix malformed markdown ("**Cross" bug etc.)
  clean = sanitiseContent(clean);

  if (!match) return { cleanContent: clean, followUps: [] };

  try {
    const suggestions = JSON.parse(match[1].trim()) as unknown;
    if (Array.isArray(suggestions)) {
      return {
        cleanContent: clean,
        followUps: (suggestions as unknown[]).filter((s) => typeof s === 'string') as string[],
      };
    }
  } catch {
    // malformed JSON — ignore
  }
  return { cleanContent: clean, followUps: [] };
}

/* ── PDF Export ────────────────────────────────────────────── */

/** HTML-escape interpolated content (F3: prevents same-origin XSS in the export blob) */
function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

async function exportAsPdf(content: string, citations: Citation[]) {
  const sourcesHtml = citations.length > 0
    ? `<h3>Sources</h3><ol>${citations.map(c =>
        `<li><strong>${esc(c.standard)}</strong> \u2014 \u00a7${esc(c.clause)}${c.text ? `<br/><small>${esc(c.text.slice(0, 200))}</small>` : ''}</li>`
      ).join('')}</ol>`
    : '';

  const bodyHtml = content.split('\n').map(line => {
    if (line.startsWith('## ')) return `<h2>${esc(line.slice(3))}</h2>`;
    if (line.startsWith('### ')) return `<h3>${esc(line.slice(4))}</h3>`;
    if (line.startsWith('> ')) return `<blockquote>${esc(line.slice(2))}</blockquote>`;
    if (line.startsWith('- ')) return `<li>${esc(line.slice(2))}</li>`;
    if (line.trim() === '') return '<br/>';
    return `<p>${esc(line)}</p>`;
  }).join('\n');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Compliance Hub Export</title>
<style>
  body{font-family:-apple-system,system-ui,sans-serif;max-width:800px;margin:40px auto;padding:0 20px;color:#1e293b;line-height:1.6;font-size:14px}
  h1{font-size:18px;color:#3b82f6;border-bottom:2px solid #e2e8f0;padding-bottom:8px}
  h2,h3{font-size:15px;margin-top:20px}
  blockquote{border-left:3px solid #3b82f6;margin:12px 0;padding:8px 16px;background:#f8fafc}
  .footer{margin-top:32px;font-size:11px;color:#94a3b8;text-align:center}
  @media print{body{margin:20px}}
</style></head><body>
<h1>Compliance Hub \u2014 Research Export</h1>
<p style="color:#64748b;font-size:12px;">Exported ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
${bodyHtml}
${sourcesHtml}
<div class="footer">Compliance Hub \u00b7 Research aid only \u00b7 Verify against official standards</div>
</body></html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const filename = `compliance-hub-export-${new Date().toISOString().slice(0, 10)}.html`;

  // A4 (2026-06-20): native share sheet removed for document export —
  // simplified to PDF (print) / HTML download only.

  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (w) {
    w.onload = () => { setTimeout(() => { w.print(); URL.revokeObjectURL(url); }, 300); };
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}

/* ── Corpus-gap standard name extractor ──────────────────────── */

/**
 * Scan the response content for corpus-gap indicators (⬜ / ❌) and
 * extract referenced standard names. Returns unique names found.
 */
function extractGapStandards(content: string): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  // Match lines starting with ⬜ or ❌ — the standard name is typically
  // the first recognizable standard-like token (e.g. "AS 3600", "NCC 2022",
  // "AS/NZS 3000:2018"). Fall back to the whole trimmed text after the emoji.
  const lineRegex = /^[\s]*[⬜❌]\s*(.+)$/gm;
  // Skip coverage labels that aren't standard names
  const SKIP_RE = /^(not retrieved|not in corpus|covered|referenced|why it matters|impact|missing|gap|corpus coverage)/i;
  let m: RegExpExecArray | null;
  while ((m = lineRegex.exec(content)) !== null) {
    const lineText = m[1].trim();
    // Skip coverage category labels (e.g. "Not retrieved: ...", "Not in corpus")
    if (SKIP_RE.test(lineText)) continue;
    // Cross-reference lines may embed the target standard deeper in the text
    // e.g. "Cross-reference: Clause 4.2 references AS 3600:2018, which is not..."
    const stdPattern = /(?:AS\/NZS|AS|NZS|NCC|BCA|ISO|IEC|EN|BS|IEEE|NFPA)[\s]*[\d]+(?:[.:][\d]+)*(?::?[\d]{4})?/gi;
    const allMatches = lineText.match(stdPattern);
    if (allMatches && allMatches.length > 0) {
      for (const match of allMatches) {
        const name = match.trim();
        if (name && !seen.has(name.toLowerCase())) {
          seen.add(name.toLowerCase());
          names.push(name);
        }
      }
    } else {
      // Fallback: use text before dash — only if it looks like a standard
      const name = lineText.split(/\s*[—–\-]\s*/)[0].trim();
      if (name && /\d/.test(name) && !seen.has(name.toLowerCase())) {
        seen.add(name.toLowerCase());
        names.push(name);
      }
    }
  }
  return names;
}

/* ── Component ───────────────────────────────────────────────── */

export default function Message({ message, onCitationClick, onShowSources, isLast, onFollowUp, queryText, onBookmarkChange, highlightRefreshKey = 0, onHighlightRemoved }: Props) {
  const { id, role, content, citations, isStreaming, error, queryId, usage, elapsed } = message;
  const components = useCitationComponents(id, onCitationClick, onFollowUp);
  const { addToast } = useToast();

  // Parse FOLLOW_UPS block out of content before rendering
  const { cleanContent, followUps } = useMemo(
    () => parseFollowUps(content),
    [content],
  );

  // Detect corpus-gap standards for the RequestStandard CTA
  // Filter out standards that are already cited (false positives from partial coverage lines)
  const gapStandards = useMemo(() => {
    if (!cleanContent) return [];
    const gaps = extractGapStandards(cleanContent);
    if (gaps.length === 0 || !citations || citations.length === 0) return gaps;
    // Build a set of cited standard prefixes for matching
    const citedSet = new Set(citations.map(c => c.standard?.toLowerCase().replace(/[:\s]+/g, ' ').trim()).filter(Boolean));
    return gaps.filter(g => {
      const gNorm = g.toLowerCase().replace(/[:\s]+/g, ' ').trim();
      // Keep the gap if no citation mentions this standard
      return !Array.from(citedSet).some(cited => cited.includes(gNorm) || gNorm.includes(cited));
    });
  }, [cleanContent, citations]);
  const [dismissedGaps, setDismissedGaps] = useState<Set<string>>(new Set());

  // P6: report form toggle + disable re-reporting after submission
  const [showReport, setShowReport] = useState(false);
  const [reported, setReported] = useState(false);

  if (role === 'user') {
    return (
      <div className="message message--user">
        <div className="message-bubble">{content}</div>
      </div>
    );
  }

  /* ── Assistant message ── */
  return (
    <div
      className="message message--assistant"
      data-msg-id={id}
      data-query-id={message.queryId || ''}
      data-session-context={queryText ? queryText.slice(0, 80) : ''}
    >
      <div className="message-bubble">
        {/* Highlight overlay — renders saved highlights on the prose */}
        {!isStreaming && !error && cleanContent && onHighlightRemoved && (
          <HighlightOverlay
            messageId={id}
            refreshKey={highlightRefreshKey}
            onHighlightRemoved={onHighlightRemoved}
          />
        )}
        {/* G1: Corpus staleness badge — shown when corpus was updated after this answer */}
        {!isStreaming && !error && message.corpusStale && (
          <div className="corpus-stale-badge" title="The standards corpus has been updated since this answer was generated. Re-run the query for the latest sources.">
            ⚠️ Corpus updated since this answer — consider re-running
          </div>
        )}

        {/* G4: Cache indicator — shown when response served from query cache */}
        {!isStreaming && !error && message.cached && (
          <div className="cache-hit-badge" title="This response was served from cache (identical query answered recently).">
            ⚡ Cached response — answered instantly
          </div>
        )}

        {/* Confidence badge + low-confidence warning */}
        {!isStreaming && !error && message.confidence && (
          <div className="confidence-header">
            <ConfidenceBadge
              confidence={message.confidence}
              reason={message.confidenceReason}
              queryId={message.queryId}
              queryText={message.queryText}
              responseText={message.content}
            />
            {(message.confidence === 'LOW' || message.confidence === 'INSUFFICIENT') && (
              <div className="confidence-warning-banner">
                ⚠️ Limited corpus coverage for this topic. Verify with the full standard.
              </div>
            )}
          </div>
        )}

        {cleanContent && (
          <div className={`message-prose${isStreaming && content.length > 0 ? ' message-prose--streaming' : ''}`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {cleanContent}
            </ReactMarkdown>
          </div>
        )}

        {/* Checklist — rendered when structured checklist data is present */}
        {!isStreaming && !error && message.checklist && (
          <Checklist
            checklistId={queryId || id}
            data={message.checklist}
            onCrossRef={onFollowUp}
          />
        )}

        {/* Request Standard CTA — shown when corpus gaps detected */}
        {!isStreaming && !error && gapStandards.length > 0 && (
          <div className="request-standard-list">
            {gapStandards
              .filter((name) => !dismissedGaps.has(name))
              .map((name) => (
                <RequestStandard
                  key={name}
                  standardName={name}
                  onDismiss={() =>
                    setDismissedGaps((prev) => new Set(prev).add(name))
                  }
                />
              ))}
          </div>
        )}

        {error && (
          error.startsWith('Daily research limit') || error.startsWith('Rate limit') ? (
            <div className="message-info-banner" role="status">
              <span className="info-banner-icon" aria-hidden="true">Info</span>
              <span>{error}</span>
            </div>
          ) : (
            <div className="message-error" role="alert">
              <span>{error}</span>
              <button
                className="retry-btn"
                onClick={() => onCitationClick(id, -1)}
                aria-label="Retry query"
              >
                Retry
              </button>
            </div>
          )
        )}

        {/* Mobile: show "N sources" button */}
        {!isStreaming && citations.length > 0 && onShowSources && (
          <button className="mobile-sources-btn" onClick={onShowSources}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            {citations.length} source{citations.length !== 1 ? 's' : ''}
          </button>
        )}

        {/* Action bar: feedback + copy + export + report */}
        {!isStreaming && !error && cleanContent && (
          <div className="message-actions">
            {queryId && <FeedbackBar queryId={queryId} />}
            {queryId && (
              <BookmarkButton
                queryId={queryId}
                query={queryText || '(Query)'}
                responsePreview={cleanContent.slice(0, 300)}
                citations={citations.map((c) => `${c.standard} \u00a7${c.clause}`)}
                onToggle={onBookmarkChange ? () => onBookmarkChange() : undefined}
              />
            )}
            <ShareMenu
              queryId={queryId}
              answerText={cleanContent}
              onToast={(msg) => addToast('success', msg, 2000)}
            />
            <button
              className="action-btn"
              onClick={(e) => {
                const btn = e.currentTarget;
                navigator.clipboard.writeText(stripMarkdown(cleanContent)).then(
                  () => {
                    btn.classList.add('action-btn--copied');
                    const origHTML = btn.innerHTML;
                    btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Copied';
                    setTimeout(() => {
                      btn.innerHTML = origHTML;
                      btn.classList.remove('action-btn--copied');
                    }, 1500);
                  },
                  () => { /* fallback: ignore */ },
                );
              }}
              aria-label="Copy response"
              title="Copy to clipboard (plain text)"
            >
              Copy
            </button>
            {/* ShareMenu replaces the old inline Copy-link/Share button above */}
            <button
              className="action-btn"
              onClick={async () => { await exportAsPdf(cleanContent, citations); addToast('success', 'Export ready', 3000); }}
              aria-label="Export as PDF"
              title="Export as PDF"
            >
              Export
            </button>
            <button
              className={`action-btn action-btn--report${showReport ? ' action-btn--active' : ''}${reported ? ' action-btn--reported' : ''}`}
              onClick={() => { if (!reported) setShowReport((v) => !v); }}
              aria-label={reported ? 'Problem reported' : 'Report a problem'}
              aria-expanded={showReport}
              title={reported ? 'Problem reported' : 'Report a problem'}
              disabled={reported}
            >
              <svg className="report-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </svg>
              {reported ? 'Reported' : 'Report'}
            </button>
          </div>
        )}

        {/* P6: Inline report form */}
        {!isStreaming && !error && showReport && !reported && (
          <ReportIssue
            queryId={queryId}
            queryText={queryText}
            responseText={cleanContent}
            onClose={() => setShowReport(false)}
            onSubmitted={() => setReported(true)}
          />
        )}

        {/* Fix 12: Disclaimer below each response */}
        {!isStreaming && !error && cleanContent && (
          <p className="message-disclaimer">
            This is a research tool, not professional advice. Always verify with a qualified professional.
          </p>
        )}

        {/* Follow-up chips — inline at bottom of response */}
        {!isStreaming && !error && followUps.length > 0 && onFollowUp && (
          <div className={`follow-up-chips follow-up-chips--inline${isLast ? '' : ' follow-up-chips--faded'}`} role="list" aria-label="Follow-up suggestions">
            {followUps.map((suggestion) => (
              <button
                key={suggestion}
                className="follow-up-chip"
                role="listitem"
                onClick={() => onFollowUp(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {/* Response time — visible to all users */}
        {!isStreaming && !error && elapsed !== undefined &&
          localStorage.getItem('standards-user-role') !== 'admin' && (
          <div className="response-time" aria-label="Response time">
            Answered in {(elapsed / 1000).toFixed(0)}s
          </div>
        )}

        {/* Usage footer — admin only (Fix 10): full stats including response time */}
        {!isStreaming && !error && usage &&
          localStorage.getItem('standards-user-role') === 'admin' && (
          <div className="usage-footer" aria-label="Response stats">
            {formatUsageFooter(usage, elapsed)}
          </div>
        )}
      </div>
    </div>
  );
}
